// ─────────────────────────────────────────────────────────────────
// Phase D + E — Helpers d'etat du Parcours Cliente (8 etapes)
// Date : 2026-05-10
//
// Le parcours cliente est un wizard linaire : 8 etapes verrouillees
// (Anamnese → Analyses → Attente → Resultats → Generation plan →
//  Edition plan → Livraison → Suivi). L'etat vit dans le JSONB
// `clients.journey_state` (cf. migration D.0).
//
// Conditions de deblocage :
//   - anamnesis : toujours active au depart
//   - analyses : anamnesis_validated
//   - waiting_results : analyses_validated (skip → saute direct a plan_generation)
//   - results : results_received
//   - plan_generation : analyses_skipped OR results_validated
//   - plan_editing : plan_generated
//   - delivery : plan_validated
//   - followup : delivered
//
// Pas de "navigation libre" : seule l'etape current_step est active,
// les autres sont locked / validated / skipped.
// ─────────────────────────────────────────────────────────────────

import { supabase } from '../supabaseClient';
import { trackStepTransition } from './observability';
import { clientAppFetch } from './clientAppFetch';
import { clientIdentityFields } from './clientIdentity';
import { isAnamneseFilled } from './anamneseAnalyzer';
import { assertPlanClinicallyCleared } from './clinicalClearance';
import { getPackAccess } from './packSystem';
import { hasRiskProfileAssessment } from './riskProfileFlags';
import { validateExternalPlanAuthority } from './externalPlanAuthority';
import { isExternalPlanInput } from './externalPlanWorkflow';
import {
  buildPlanDeliveryContractV1,
  PLAN_DELIVERY_CONTRACT_VERSION,
  validatePlanDeliveryEvidence,
} from './planDeliveryContract';

export function isSameInstant(left, right) {
  const leftMs = Date.parse(left || '');
  const rightMs = Date.parse(right || '');
  return Number.isFinite(leftMs) && Number.isFinite(rightMs) && leftMs === rightMs;
}

/**
 * Une étape du fil est-elle consultable en un clic ?
 *
 * Revue ergo 2026-08-21 — cette règle exigeait auparavant un parcours terminé
 * (`followup_started`). Une praticienne qui vient de publier ne porte pas
 * encore ce drapeau : le fil était donc mort au moment précis où elle
 * cherchait à revenir sur la Livraison, et une flèche « ← » sans libellé
 * restait sa seule sortie. Consulter n'écrit rien et ne déplace pas le
 * curseur, donc rien ne justifiait de l'interdire plus tôt.
 *
 * Une étape verrouillée reste fermée : il n'y a rien à y voir.
 */
export function isStepConsultable(journey, step, currentStep) {
  if (!step || step === currentStep) return false;
  const status = getStepStatus(journey, step);
  return status === 'validated' || status === 'skipped';
}

export function resolveJourneyCompletionPresentation(currentStep, journey, packAccess) {
  const isOneShotTerminal = currentStep === 'followup'
    && journey?.delivered === true
    && packAccess?.known === true
    && packAccess?.appMode === 'oneshot'
    && packAccess?.continuousFollowup !== true;
  const isParcoursComplete = currentStep === 'followup'
    && (journey?.followup_started === true || isOneShotTerminal);
  const steps = journeyStepsForState(journey);
  const currentStepIndex = Math.max(1, steps.indexOf(currentStep) + 1);

  return {
    isOneShotTerminal,
    isParcoursComplete,
    progressPct: isParcoursComplete
      ? 100
      : Math.round(((currentStepIndex - 1) / steps.length) * 100),
  };
}

export const DELIVERY_MODES = Object.freeze({
  APP: 'app',
  DOCUMENT: 'document',
  APP_AND_DOCUMENT: 'app_and_document',
});

export const DOCUMENT_DELIVERY_CHANNELS = Object.freeze([
  'email',
  'hand_delivery',
  'post',
]);

export function normalizeDeliveryRequest({
  mode,
  documentChannel = null,
  consultationId,
  contentSha256,
} = {}) {
  if (!Object.values(DELIVERY_MODES).includes(mode)) {
    throw new Error('Choisissez comment la cliente recevra son programme');
  }
  const includesDocument = mode === DELIVERY_MODES.DOCUMENT
    || mode === DELIVERY_MODES.APP_AND_DOCUMENT;
  if (!includesDocument && documentChannel !== null) {
    throw new Error('Canal document inattendu');
  }
  if (includesDocument && !DOCUMENT_DELIVERY_CHANNELS.includes(documentChannel)) {
    throw new Error('Choisissez comment le document a ete remis');
  }
  const normalizedConsultationId = String(consultationId || '').trim();
  const normalizedContentSha256 = String(contentSha256 || '').trim().toLowerCase();
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(normalizedConsultationId)
      || !/^[0-9a-f]{64}$/.test(normalizedContentSha256)) {
    throw new Error('La version exacte du plan livre est introuvable');
  }
  return {
    mode,
    documentChannel: includesDocument ? documentChannel : null,
    consultationId: normalizedConsultationId,
    contentSha256: normalizedContentSha256,
  };
}

export function hasAnamnesisCompletionProof(journeyState) {
  const value = journeyState?.anamnesis_completed_at;
  if (typeof value !== 'string') return false;
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value)) return false;
  try {
    return new Date(value).toISOString() === value;
  } catch {
    return false;
  }
}

export function canValidateAnamnesisStep(form, journeyState) {
  return isAnamneseFilled(form) && hasAnamnesisCompletionProof(journeyState);
}

export async function sha256Hex(value) {
  if (typeof value !== 'string' || !value.trim()) {
    throw new Error('Plan vide : validation impossible');
  }
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) throw new Error('Empreinte du plan indisponible : validation impossible');
  const digest = await subtle.digest('SHA-256', new TextEncoder().encode(value));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export function buildPlanValidationContent(planText, supplementsText = '') {
  const plan = typeof planText === 'string' ? planText : '';
  const supplements = typeof supplementsText === 'string' ? supplementsText : '';
  return `${plan}\n\n--- SUPPLEMENTS ---\n${supplements}`;
}

export function evaluatePlanValidationReadiness({
  planText,
  supplementsText = '',
  form,
  journeyState,
} = {}) {
  if (!canValidateAnamnesisStep(form, journeyState)) {
    return {
      cleared: false,
      severity: 'high',
      violations: [{
        type: 'anamnesis_unconfirmed',
        severity: 'high',
        label: 'Anamnèse complète et validation professionnelle requises avant le plan.',
      }],
      warnings: [],
    };
  }
  if (!hasRiskProfileAssessment(form)) {
    return {
      cleared: false,
      severity: 'high',
      violations: [{
        type: 'risk_profile_assessment_missing',
        severity: 'high',
        label: 'Évaluation des profils à risque à confirmer par Anissa avant validation du plan.',
      }],
      warnings: [],
    };
  }
  const verdict = assertPlanClinicallyCleared(
    buildPlanValidationContent(planText, supplementsText),
    { form },
  );
  if (verdict.cleared === true && Array.isArray(verdict.warnings) && verdict.warnings.length > 0) {
    return {
      ...verdict,
      cleared: false,
      severity: 'high',
      violations: verdict.warnings.map((warning) => ({
        ...warning,
        type: `manual_${warning.type || 'review'}`,
        severity: 'high',
        label: `À valider par Anissa : ${warning.label || 'contrôle clinique requis'}`,
      })),
    };
  }
  return verdict;
}

export class PlanValidationClinicalError extends Error {
  constructor(verdict) {
    super('Clairance clinique refusée : corrige le plan avant de le valider.');
    this.name = 'PlanValidationClinicalError';
    this.verdict = verdict;
  }
}

export const ANISSA_CLINICAL_OVERRIDE_DECISION = 'anissa_clinical_override';

function hasExactObjectKeys(value, expected) {
  return value && typeof value === 'object' && !Array.isArray(value)
    && Object.keys(value).sort().join('|') === [...expected].sort().join('|');
}

function isClinicalSnapshotItem(item) {
  return item && typeof item === 'object' && !Array.isArray(item)
    && typeof item.type === 'string' && item.type.trim() !== ''
    && typeof item.severity === 'string' && item.severity.trim() !== ''
    && typeof item.label === 'string' && item.label.trim() !== '';
}

export function buildPlanClinicalOverridePayload(verdict) {
  if (!verdict || verdict.cleared !== false
      || typeof verdict.severity !== 'string' || verdict.severity.trim() === ''
      || !Array.isArray(verdict.violations) || verdict.violations.length === 0
      || !Array.isArray(verdict.warnings)
      || verdict.violations.some((item) => !isClinicalSnapshotItem(item))
      || verdict.warnings.some((item) => !isClinicalSnapshotItem(item))) {
    throw new Error('Verdict clinique bloquant requis pour confirmer un override.');
  }
  // Une clairance impossible (entrée illisible, contrôle en échec) est un
  // fail-closed technique, pas un avis clinique : elle ne se confirme jamais.
  if (verdict.violations.some((item) => item.type === 'clearance_error')) {
    throw new Error('Contrôle clinique impossible : recharge la fiche puis réessaie. Aucune confirmation ne peut le remplacer.');
  }
  // Prérequis de dossier, pas des alertes cliniques : ils se complètent dans
  // la fiche (anamnèse, profils à risque), ils ne se confirment pas.
  const PREREQUISITES = ['anamnesis_unconfirmed', 'risk_profile_assessment_missing', 'risk_profile_unknown'];
  if (verdict.violations.some((item) => PREREQUISITES.includes(item.type))) {
    throw new Error('Complète d’abord la fiche (anamnèse validée, profils à risque évalués) : ce prérequis ne se confirme pas.');
  }
  const snapshot = JSON.parse(JSON.stringify({
    cleared: verdict.cleared,
    severity: verdict.severity,
    violations: verdict.violations,
    warnings: verdict.warnings,
  }));
  const violationTypes = [...new Set(snapshot.violations.map((item) => item?.type))]
    .filter((type) => typeof type === 'string' && type.trim() !== '')
    .sort();
  if (violationTypes.length === 0) {
    throw new Error('Snapshot clinique invalide pour confirmer un override.');
  }
  return {
    version: 1,
    decision: ANISSA_CLINICAL_OVERRIDE_DECISION,
    confirmed: true,
    verdict: snapshot,
    violation_types: violationTypes,
  };
}

export function isCoherentPlanClinicalDecisionProof(proof, {
  consultationId,
  contentSha256,
  formSha256 = proof?.form_sha256,
} = {}) {
  if (!proof || typeof proof !== 'object' || Array.isArray(proof)
      || !consultationId || proof.consultation_id !== consultationId
      || !/^[a-f0-9]{64}$/.test(contentSha256 || '')
      || proof.content_sha256 !== contentSha256
      || !/^[a-f0-9]{64}$/.test(formSha256 || '')
      || proof.form_sha256 !== formSha256
      || typeof proof.validated_by !== 'string' || proof.validated_by.trim() === ''
      || !Number.isFinite(Date.parse(proof.validated_at || ''))) return false;
  if (proof.decision === 'cleared_no_warning') {
    return proof.clinical_override === undefined;
  }
  // V97.247 : override scellé pour les plans standard et externes, jamais MGD.
  if (proof.decision !== ANISSA_CLINICAL_OVERRIDE_DECISION
      || !['standard', 'external'].includes(proof.authority_kind)) return false;
  const override = proof.clinical_override;
  if (!hasExactObjectKeys(override, [
    'actor_id', 'audit_id', 'confirmed_at', 'consultation_id', 'content_sha256',
    'form_sha256', 'snapshot', 'version',
  ]) || override.version !== 1
      || override.actor_id !== proof.validated_by
      || override.consultation_id !== consultationId
      || override.content_sha256 !== contentSha256
      || override.form_sha256 !== formSha256
      || !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
        .test(override.audit_id || '')
      || !Number.isFinite(Date.parse(override.confirmed_at || ''))) return false;
  const snapshot = override.snapshot;
  if (!hasExactObjectKeys(snapshot, [
    'confirmed', 'decision', 'verdict', 'version', 'violation_types',
  ]) || snapshot.version !== 1 || snapshot.confirmed !== true
      || snapshot.decision !== ANISSA_CLINICAL_OVERRIDE_DECISION
      || !hasExactObjectKeys(snapshot.verdict, [
        'cleared', 'severity', 'violations', 'warnings',
      ]) || snapshot.verdict.cleared !== false
      || typeof snapshot.verdict.severity !== 'string'
      || snapshot.verdict.severity.trim() === ''
      || !Array.isArray(snapshot.verdict.violations)
      || snapshot.verdict.violations.length === 0
      || !Array.isArray(snapshot.verdict.warnings)
      || snapshot.verdict.violations.some((item) => !isClinicalSnapshotItem(item))
      || snapshot.verdict.warnings.some((item) => !isClinicalSnapshotItem(item))
      || !Array.isArray(snapshot.violation_types)) return false;
  const expectedTypes = [...new Set(snapshot.verdict.violations.map((item) => item?.type))]
    .filter((type) => typeof type === 'string' && type.trim() !== '')
    .sort();
  return expectedTypes.length > 0
    && snapshot.violation_types.every((type) => typeof type === 'string' && type.trim() !== '')
    && snapshot.violation_types.join('|') === expectedTypes.join('|');
}

// V97.4 (2026-05-12) — Sync auto SaaS BC.5 → app cliente journey_status.
// Avant ce mapping, Anissa devait avancer manuellement la timeline cliente
// via le JourneyCockpit en plus de sa propre timeline SaaS. Risque énorme
// d'oublis + clientes bloquées en 'welcome' (cas Camille 2026-05-12).
//
// Maintenant : chaque transition SaaS pousse automatiquement l'état cliente
// correspondant via clientAppFetch (best-effort, ne bloque jamais la
// transition SaaS si la sync échoue — l'observability tracking continue).
//
// Mapping métier (cf. cartographie d'audit 2026-05-12, mis à jour V97.13) :
//
// Note V97.13 : depuis V97.10 le RDV anamnèse est une feature distincte du
// SaaS (BLOC 3 Onboarding avec journey_state.rdv_anamnesis_at + sync push
// vers clients.rdv_scheduled_at côté app cliente). Le mapping ci-dessous
// ne gère plus rdv_scheduled/rdv_done qui sont pilotés directement par
// les actions RDV du SaaS (pas par la transition d'étape SaaS).
//
// Quand Anissa valide l'anamnèse (passage SaaS anamnesis → analyses),
// la cliente passe directement à "analyses" (Vos analyses sanguines) car
// l'étape rdv_scheduled/rdv_done est déjà gérée par les sync RDV
// précédentes (V97.10 Phase B).
const SAAS_TO_CLIENT_APP = {
  anamnesis:       'questionnaire',       // Anissa onboarde → cliente remplit pré-questionnaire
  analyses:        'analyses',            // V97.13 fix : était 'rdv_scheduled' (obsolète depuis V97.10). Anissa valide l'anamnèse + prescrit analyses → cliente bascule sur "Vos analyses sanguines"
  waiting_results: 'analyses',            // Attente résultats labo (cliente reste sur Vos analyses)
  results:         'analyses',            // Anissa saisit résultats (mêmes côté cliente)
  plan_generation: 'program_in_progress', // IA génère plan
  plan_editing:    'program_in_progress', // Anissa édite plan
  // L'app ne passe en 'program_active' QUE lors d'une vraie publication de
  // contenu (route /api/admin/publish-plan). Delivery reste donc en
  // preparation; followup ne survient qu'apres le succes de publication.
  delivery:        'program_in_progress',
  // Le passage SaaS a followup n'arrive qu'apres une publication reussie.
  // Confirmer program_active evite de degrader le statut que publish-plan
  // vient d'activer et repare son update best-effort si necessaire.
  followup:        'program_active',
};

// V97.82 — backoff util pour les retries de sync app cliente.
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// V97.82 — Écrit (marker) ou nettoie (null) journey_state.client_app_sync_pending.
// Re-lit l'état juste avant d'écrire pour ne toucher QUE cette clé et minimiser
// le clobber si une autre transition a eu lieu entre-temps. Best-effort : le
// marqueur est indicatif, il ne doit jamais faire échouer une transition.
async function recordSyncMarker(clientId, marker) {
  try {
    const { data } = await supabase
      .from('clients')
      .select('journey_state')
      .eq('id', clientId)
      .maybeSingle();
    const js = { ...(data?.journey_state || {}) };
    if (marker) {
      js.client_app_sync_pending = marker;
    } else {
      if (!js.client_app_sync_pending) return;
      delete js.client_app_sync_pending;
    }
    await supabase
      .from('clients')
      .update({ journey_state: js })
      .eq('id', clientId)
      .eq('journey_state', JSON.stringify(data?.journey_state || {}));
  } catch {
    /* marqueur indicatif : ne bloque rien */
  }
}

/**
 * Pousse l'état cliente correspondant à l'étape SaaS.
 * Ne throw jamais (les transitions SaaS ne doivent pas être bloquées si l'app
 * cliente est down ou l'env CLIENT_APP_API_URL pas configuré).
 *
 * V97.13.22 — accepte aussi des "events" pour pousser les timestamps du
 * workflow hybride (protocol_shipped, protocol_received) qui ne mappent pas
 * vers un nouveau journey_status (l'enum cliente reste 'program_in_progress'
 * pendant ces sous-états — c'est l'UI qui lit les timestamps directement).
 *
 * V97.82 — FIABILISATION : retry ×3 avec backoff (absorbe les échecs
 * transitoires) puis, en cas d'échec persistant, dépose un marqueur VISIBLE
 * (journey_state.client_app_sync_pending) pour qu'Anissa sache que la cliente
 * n'a pas été synchronisée et puisse relancer. Résout le risque « cliente
 * bloquée sans alerte » (audit parcours 2026-07-03).
 *
 * @param {string} clientId - id Supabase de la cliente côté SaaS
 * @param {string} saasStepOrEvent - clé JOURNEY_STEPS SaaS OU event spécial
 * @returns {Promise<{ ok: boolean, skipped?: boolean, error?: string }>}
 */
async function syncClientAppStatus(clientId, saasStepOrEvent) {
  const isShippedEvent = saasStepOrEvent === 'protocol_shipped';
  const isReceivedEvent = saasStepOrEvent === 'protocol_received';
  const targetStatus = SAAS_TO_CLIENT_APP[saasStepOrEvent];

  if (!targetStatus && !isShippedEvent && !isReceivedEvent) return { ok: true, skipped: true };

  const { data: client } = await supabase
    .from('clients')
    .select('form, journey_state, app_enabled, staging_client_id')
    .eq('id', clientId)
    .maybeSingle();
  const form = client?.form || {};
  // Une réponse au questionnaire par lien n'est pas une preuve d'espace app.
  // Seul le mode app explicite peut servir de compatibilité aux anciens flags.
  const questionnaireThroughApp = client?.journey_state?.questionnaire_mode === 'app'
    && Boolean(form.objectif_primaire || form.dureeProbleme || form.ressentiDigestion);
  if (client?.app_enabled !== true && !client?.staging_client_id && !questionnaireThroughApp) {
    await recordSyncMarker(clientId, null);
    return { ok: true, skipped: true };
  }
  const payload = clientIdentityFields({
    form,
    staging_client_id: client?.staging_client_id || null,
  });
  if (!payload.email && !payload.client_id) {
    await recordSyncMarker(clientId, {
      step: saasStepOrEvent,
      target: targetStatus || saasStepOrEvent,
      since: new Date().toISOString(),
      error: 'missing-client-identity',
    });
    return { ok: false, error: 'missing-client-identity' };
  }
  if (targetStatus) payload.status = targetStatus;
  if (isShippedEvent) payload.protocol_shipped_at = new Date().toISOString();
  if (isReceivedEvent) payload.protocol_received_at = new Date().toISOString();

  let lastErr = null;
  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      await clientAppFetch('/api/admin/client-journey-status', { method: 'POST', payload });
      await recordSyncMarker(clientId, null); // succès → nettoie un marqueur éventuel
      return { ok: true };
    } catch (e) {
      lastErr = e;
      if (attempt < 3) await sleep(attempt * 400);
    }
  }
  console.warn('[journeyState] syncClientAppStatus failed after retries:', lastErr?.message || lastErr);
  await recordSyncMarker(clientId, {
    step: saasStepOrEvent,
    target: targetStatus || saasStepOrEvent,
    since: new Date().toISOString(),
    error: String(lastErr?.message || lastErr).slice(0, 200),
  });
  return { ok: false, error: String(lastErr?.message || lastErr) };
}

/**
 * V97.82 — Lit le marqueur d'échec de sync app cliente (pour l'UI SaaS).
 * Fonction pure. @returns {null | { step, target, since, error }}
 */
export function getClientAppSyncPending(journeyState) {
  return journeyState?.client_app_sync_pending || null;
}

/**
 * V97.82 — Rejoue la sync app cliente restée en échec (bouton « Réessayer »
 * d'Anissa). Relit le marqueur et re-pousse l'étape concernée.
 * @returns {Promise<{ ok: boolean, nothingToRetry?: boolean, error?: string }>}
 */
export async function retryClientAppSync(clientId) {
  if (!clientId) return { ok: false, error: 'clientId requis' };
  const { data } = await supabase
    .from('clients')
    .select('journey_state')
    .eq('id', clientId)
    .maybeSingle();
  const pending = data?.journey_state?.client_app_sync_pending;
  if (!pending?.step) return { ok: true, nothingToRetry: true };
  return syncClientAppStatus(clientId, pending.step);
}

export const JOURNEY_STEPS = [
  'anamnesis',
  'analyses',
  'waiting_results',
  'results',
  'plan_generation',
  'plan_editing',
  'delivery',
  'followup',
];

export function journeyStepsForState(state = {}) {
  const usesExternalDossierFlow = state?.external_dossier_flow === true;
  return usesExternalDossierFlow
    ? JOURNEY_STEPS.filter((step) => step !== 'results')
    : JOURNEY_STEPS;
}

export const STEP_META = {
  // BB.1 (2026-05-11) : 'Anamnèse' → 'Onboarding'. La 1ère étape n'est plus
  // juste 'vérifier l'anamnèse' mais l'onboarding complet : configurer le
  // mode d'accompagnement (app, papier, poids, notifs) + envoyer le pré-
  // questionnaire + recevoir les réponses + valider. Plus large et plus
  // proche du workflow réel d'Anissa. Clé interne 'anamnesis' conservée pour
  // backward compat (clientes existantes en BDD avec current_step='anamnesis').
  anamnesis:       { index: 1, label: 'Onboarding',        icon: '🎯' },
  analyses:        { index: 2, label: 'Analyses',          icon: '🧪' },
  waiting_results: { index: 3, label: 'Réception rapports', icon: '⏳' },
  results:         { index: 4, label: 'Revue résultats',    icon: '📥' },
  plan_generation: { index: 5, label: 'Génération plan',   icon: '✨' },
  plan_editing:    { index: 6, label: 'Édition plan',      icon: '🥗' },
  delivery:        { index: 7, label: 'Livraison',         icon: '📨' },
  followup:        { index: 8, label: 'Suivi',             icon: '🔄' },
};

export const DEFAULT_JOURNEY_STATE = {
  current_step: 'anamnesis',
  anamnesis_validated: false,
  analyses_skipped: false,
  analyses_validated: false,
  results_received: false,
  results_validated: false,
  plan_generated: false,
  plan_validated: false,
  // V97.13.22 — workflow hybride physique + digital :
  //   1. protocol_shipped=true  → Anissa a expédié le courrier
  //   2. client_received_confirmed=true → cliente a cliqué "J'ai reçu" dans l'app
  //   3. delivered=true → Anissa a activé l'espace cliente (étape 8 démarre)
  // Avant V97.13.22 : delivered=true tout de suite, sans étape physique.
  protocol_shipped: false,
  protocol_shipped_at: null,         // timestamp ISO
  client_received_confirmed: false,
  protocol_received_at: null,        // timestamp ISO
  delivered: false,                  // = espace cliente activé (workflow final)
  followup_started: false,
};

/**
 * Statut visuel d'une etape pour la sidebar.
 * Returns 'validated' | 'active' | 'locked' | 'skipped'
 */
// Backwards compat : les clientes pre-Phase E ont current_step='analyses'
// (ancien defaut Phase D) sans anamnesis_validated explicite. Si on est deja
// dans un step post-anamnesis, on considere l'anamnese comme implicitement
// faite — evite une migration data SQL pour les 5 clientes existantes.
const POST_ANAMNESIS_STEPS = ['analyses', 'waiting_results', 'results', 'plan_generation', 'plan_editing', 'delivery', 'followup'];

export function getStepStatus(state, step) {
  const s = { ...DEFAULT_JOURNEY_STATE, ...(state || {}) };
  const isActive = (k) => s.current_step === k;
  const effectiveAnamnesisValidated = s.anamnesis_validated || POST_ANAMNESIS_STEPS.includes(s.current_step);

  switch (step) {
    case 'anamnesis':
      if (effectiveAnamnesisValidated) return 'validated';
      return isActive('anamnesis') ? 'active' : 'locked';

    case 'analyses':
      if (s.analyses_skipped) return 'skipped';
      if (s.analyses_validated) return 'validated';
      if (!effectiveAnamnesisValidated) return 'locked';
      return isActive('analyses') ? 'active' : 'locked';

    case 'waiting_results':
      if (s.analyses_skipped) return 'skipped';
      if (s.results_received) return 'validated';
      if (!s.analyses_validated) return 'locked';
      return isActive('waiting_results') ? 'active' : 'locked';

    case 'results':
      if (s.analyses_skipped) return 'skipped';
      if (s.results_validated) return 'validated';
      if (!s.results_received) return 'locked';
      return isActive('results') ? 'active' : 'locked';

    case 'plan_generation': {
      if (s.plan_generated || s.external_synthesis_ready) return 'validated';
      const unlocked = s.analyses_skipped || s.results_validated
        || (s.external_dossier_flow === true && s.results_received === true);
      if (!unlocked) return 'locked';
      return isActive('plan_generation') ? 'active' : 'locked';
    }

    case 'plan_editing':
      if (s.plan_validated) return 'validated';
      if (!s.plan_generated && !s.external_synthesis_ready) return 'locked';
      return isActive('plan_editing') ? 'active' : 'locked';

    case 'delivery':
      if (s.delivered) return 'validated';
      if (!s.plan_validated) return 'locked';
      return isActive('delivery') ? 'active' : 'locked';

    case 'followup':
      if (s.followup_started) return 'validated';
      if (!s.delivered) return 'locked';
      return isActive('followup') ? 'active' : 'locked';

    default:
      return 'locked';
  }
}

/**
 * Patch partiel du journey_state. Merge cote serveur via jsonb_set,
 * mais ici on relit + reecrit l'objet entier (simple, suffisant tant
 * qu'on a 1 seul ecrivain a la fois — le wizard).
 */
/**
 * 2026-08-08 — Zéro ligne renvoyée par la RLS a DEUX causes très
 * différentes, et « recharge la page » n'en soigne qu'une seule :
 *
 *  1. la session Supabase est morte → la requête part en rôle `anon`, la
 *     policy de `clients` est réservée à `authenticated`, donc toutes les
 *     lignes sont invisibles. Recharger ne change rien : il faut se
 *     reconnecter.
 *  2. la session est vivante mais la fiche n'existe pas (ou plus) en base
 *     — typiquement une fiche affichée depuis la copie locale.
 *
 * On ne paie ce diagnostic que sur le chemin d'échec.
 */
async function expliquerLigneInvisible() {
  try {
    const { data, error } = await supabase.auth.getUser();
    if (!data?.user && (!error || error.name !== 'AuthRetryableFetchError')) {
      return 'Ta session a expiré. Reconnecte-toi pour enregistrer cette étape.';
    }
  } catch { /* on retombe sur le message générique */ }
  return 'La fiche cliente n’est plus accessible en base. Recharge la page.';
}

const JSON_COMPARE_MAX_DEPTH = 32;
const JSON_COMPARE_MAX_NODES = 10000;

/** Compare deux valeurs JSON sans dépendre de l'ordre des clés JSONB. */
export function areJsonValuesStructurallyEqual(left, right, limits = {}) {
  const maxDepth = Number.isInteger(limits.maxDepth) && limits.maxDepth >= 0
    ? limits.maxDepth : JSON_COMPARE_MAX_DEPTH;
  const maxNodes = Number.isInteger(limits.maxNodes) && limits.maxNodes > 0
    ? limits.maxNodes : JSON_COMPARE_MAX_NODES;
  let nodes = 0;
  const leftPath = new WeakSet();
  const rightPath = new WeakSet();

  function compare(a, b, depth) {
    nodes += 1;
    if (nodes > maxNodes || depth > maxDepth) return false;
    if (a === b) return a === null || typeof a !== 'number' || Number.isFinite(a);
    if (a === null || b === null || typeof a !== typeof b || typeof a !== 'object') return false;

    const aIsArray = Array.isArray(a);
    if (aIsArray !== Array.isArray(b) || leftPath.has(a) || rightPath.has(b)) return false;
    leftPath.add(a);
    rightPath.add(b);
    try {
      if (aIsArray) {
        if (a.length !== b.length) return false;
        for (let index = 0; index < a.length; index += 1) {
          if (!compare(a[index], b[index], depth + 1)) return false;
        }
        return true;
      }
      const aPrototype = Object.getPrototypeOf(a);
      const bPrototype = Object.getPrototypeOf(b);
      if ((aPrototype !== Object.prototype && aPrototype !== null)
          || (bPrototype !== Object.prototype && bPrototype !== null)) return false;
      const aKeys = Object.keys(a);
      const bKeys = Object.keys(b);
      if (aKeys.length !== bKeys.length) return false;
      for (const key of aKeys) {
        if (!Object.hasOwn(b, key) || !compare(a[key], b[key], depth + 1)) return false;
      }
      return true;
    } finally {
      leftPath.delete(a);
      rightPath.delete(b);
    }
  }

  return compare(left, right, 0);
}

export async function updateJourneyState(clientId, patch) {
  if (!clientId) throw new Error('clientId requis');
  if (!patch || typeof patch !== 'object' || Array.isArray(patch)
      || Object.hasOwn(patch, 'consultations_log')) {
    throw new Error('Patch du parcours invalide : le journal des rendez-vous est en lecture seule.');
  }
  const { data, error } = await supabase.rpc('mettre_a_jour_parcours_client', {
    p_client_id: clientId,
    p_patch: patch,
  });
  if (error) throw new Error(error.message);
  const persisted = data;
  const patchPersisted = persisted && Object.entries(patch).every(([key, value]) => (
    Object.hasOwn(persisted, key) && areJsonValuesStructurallyEqual(persisted[key], value)
  ));
  if (!patchPersisted) {
    throw new Error('La transition n’a pas été enregistrée. Recharge la fiche et réessaie.');
  }
  return persisted;
}

/**
 * V97.39.8 (roadmap 1.1) — Parcours therapeutique accepte AVANT qu'une
 * consultation hote n'existe (cas pack Bilan : page Suivi atteinte sans plan,
 * cf. melissa). On range les phases dans journey_state.pending_protocol_phases
 * au lieu de creer une consultation (qui consommerait le compteur de pack).
 * Elles seront greffees sur la 1ere consultation creee (cf.
 * bakePendingProtocolPhases dans protocolPhases.js).
 *
 * updateJourneyState preserve les cles arbitraires (merge {...DEFAULT,
 * ...current, ...patch}) → pending_protocol_phases ne necessite AUCUNE
 * migration SQL.
 */
/**
 * 2026-07-26 — Module « Préparer l'appel résultats » : note d'appel datée,
 * append-only dans journey_state.results_call_notes = [{ date, text }].
 * Même pattern que consultations_log (lecture + merge de l'array, sinon
 * updateJourneyState écraserait la clé). Remontée ensuite dans le briefing
 * pré-RDV (« Dernier appel résultats »).
 */
export async function logResultsCallNote(clientId, text) {
  if (!clientId) throw new Error('clientId requis');
  const clean = (text || '').trim();
  if (!clean) throw new Error('Note vide');
  const { data: current, error: loadErr } = await supabase
    .from('clients')
    .select('journey_state')
    .eq('id', clientId)
    .maybeSingle();
  if (loadErr) throw new Error(loadErr.message);
  const state = current?.journey_state || {};
  const log = Array.isArray(state.results_call_notes) ? state.results_call_notes : [];
  const entry = { date: new Date().toISOString(), text: clean };
  await updateJourneyState(clientId, { results_call_notes: [...log, entry] });
  return entry;
}

export async function setPendingProtocolPhases(clientId, protocolPhases) {
  return updateJourneyState(clientId, { pending_protocol_phases: protocolPhases || null });
}

export async function clearPendingProtocolPhases(clientId) {
  return updateJourneyState(clientId, { pending_protocol_phases: null });
}

/**
 * Transitions metier pour les 8 etapes.
 * Chaque transition emet un event step_transition (best-effort, ne bloque jamais)
 * pour la couche observabilite — captures temps reel d'utilisation Anissa.
 */
/**
 * Aligne le statut du plan d'analyses sur l'avancement du parcours.
 *
 * 25.08.2026 — La carte du plan portait son propre bouton de transition, qui
 * proposait « Resultats recus » sur l'etape ou le parcours demandait de
 * passer EN ATTENTE de resultats. Deux boutons verts contradictoires pour
 * une seule decision. Le bouton a ete retire ; sans ce relais, plus rien ne
 * ferait avancer le statut, et les compteurs du tableau de bord d'Anissa
 * resteraient a zero pour toujours.
 *
 * Le parcours decide, le plan suit. Une seule decision, deux traces
 * cohérentes.
 *
 * ⚠ Ne fait JAMAIS echouer la transition du parcours : le statut du plan est
 * un reflet, pas une source. Si l'ecriture rate, l'avancement reste juste et
 * seul un compteur est en retard.
 *
 * @param {string} clientId
 * @param {'sent'|'in_progress'|'completed'} statut
 */
async function synchroniserStatutPlan(clientId, statut) {
  try {
    const { data } = await supabase
      .from('analysis_plans')
      .select('id,status')
      .eq('client_id', clientId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    // Pas de plan, ou deja au bon statut : rien a faire. On ne redescend
    // jamais non plus — un parcours qui recule ne doit pas rouvrir un plan
    // que la cliente a deja consomme.
    const ordre = ['draft', 'sent', 'in_progress', 'completed'];
    if (!data?.id) return;
    if (ordre.indexOf(statut) <= ordre.indexOf(data.status)) return;
    await supabase.from('analysis_plans').update({ status: statut }).eq('id', data.id);
  } catch (e) {
    console.warn('[journeyState] statut du plan non synchronise:', e?.message || e);
  }
}

export const transitions = {
  // Etape 1 → 2
  validateAnamnesis: async (clientId) => {
    if (!clientId) throw new Error('clientId requis');
    const { data: current, error: loadError } = await supabase
      .from('clients')
      .select('journey_state, pack_type, form')
      .eq('id', clientId)
      .maybeSingle();
    if (loadError) throw new Error(loadError.message);
    if (!current) throw new Error(await expliquerLigneInvisible());
    if (getPackAccess(current.pack_type).known !== true) {
      throw new Error('Pack absent ou inconnu : validation de l anamnese impossible');
    }

    const currentJourney = current.journey_state || {};
    if (
      currentJourney.anamnesis_validated === true &&
      POST_ANAMNESIS_STEPS.includes(currentJourney.current_step)
    ) {
      return currentJourney;
    }
    if (currentJourney.current_step !== 'anamnesis') {
      throw new Error('L anamnese ne peut etre validee que depuis son etape active');
    }
    if (!isAnamneseFilled(current.form)) {
      throw new Error('Complete d abord les informations essentielles de l anamnese');
    }
    if (!hasAnamnesisCompletionProof(currentJourney)) {
      throw new Error('Marque d abord l anamnese comme terminee');
    }

    const next = {
      ...DEFAULT_JOURNEY_STATE,
      ...currentJourney,
      anamnesis_validated: true,
      current_step: 'analyses',
    };
    const { data: persisted, error: persistError } = await supabase
      .from('clients')
      .update({ journey_state: next })
      .eq('id', clientId)
      .eq('journey_state', JSON.stringify(currentJourney))
      .select('journey_state')
      .maybeSingle();
    if (persistError) throw new Error(persistError.message);
    if (!persisted?.journey_state) {
      throw new Error('L anamnese a ete modifiee ailleurs. Recharge la fiche et reessaie.');
    }
    const r = persisted.journey_state;
    if (r.anamnesis_validated !== true || r.current_step !== 'analyses') {
      throw new Error('La validation de l anamnese n a pas ete enregistree');
    }
    trackStepTransition({ clientId, fromStep: 'anamnesis', toStep: 'analyses' });
    syncClientAppStatus(clientId, 'analyses'); // V97.4 — sync auto
    return r;
  },
  // Etape 2 → 3
  validateAnalyses: async (clientId) => {
    const r = await updateJourneyState(clientId, {
      analyses_validated: true,
      external_dossier_flow: true,
      current_step: 'waiting_results',
    });
    trackStepTransition({ clientId, fromStep: 'analyses', toStep: 'waiting_results' });
    syncClientAppStatus(clientId, 'waiting_results');
    // La prescription est partie chez la cliente : le plan est « envoye ».
    synchroniserStatutPlan(clientId, 'sent');
    return r;
  },
  // Etape 2 skip → 5 (saute attente, resultats — direct generation plan)
  skipAnalyses: async (clientId) => {
    const r = await updateJourneyState(clientId, { analyses_skipped: true, current_step: 'plan_generation' });
    trackStepTransition({ clientId, fromStep: 'analyses', toStep: 'plan_generation', direction: 'skip' });
    syncClientAppStatus(clientId, 'plan_generation');
    return r;
  },
  // Etape 3 → 4
  markResultsReceived: async (clientId) => {
    const r = await updateJourneyState(clientId, { results_received: true, current_step: 'results' });
    trackStepTransition({ clientId, fromStep: 'waiting_results', toStep: 'results' });
    syncClientAppStatus(clientId, 'results');
    synchroniserStatutPlan(clientId, 'in_progress');
    return r;
  },
  // Nouveau flux externe : la réception locale ouvre directement la synthèse.
  // Les dossiers déjà positionnés sur `results` gardent leur revue historique.
  markResultsReceivedToExternalSynthesis: async (clientId, journeyState) => {
    if (journeyState?.external_dossier_flow !== true) {
      throw new Error('Le parcours externe doit être activé avant de continuer vers la synthèse.');
    }
    const r = await updateJourneyState(clientId, {
      results_received: true,
      external_dossier_flow: true,
      current_step: 'plan_generation',
    });
    trackStepTransition({ clientId, fromStep: 'waiting_results',
      toStep: 'plan_generation', direction: 'skip' });
    syncClientAppStatus(clientId, 'plan_generation');
    return r;
  },
  // Etape 4 → 5
  validateResults: async (clientId) => {
    const r = await updateJourneyState(clientId, { results_validated: true, current_step: 'plan_generation' });
    trackStepTransition({ clientId, fromStep: 'results', toStep: 'plan_generation' });
    syncClientAppStatus(clientId, 'plan_generation');
    return r;
  },
  // Etape 5 → 6
  markPlanGenerated: async (clientId) => {
    const r = await updateJourneyState(clientId, { plan_generated: true, current_step: 'plan_editing' });
    trackStepTransition({ clientId, fromStep: 'plan_generation', toStep: 'plan_editing' });
    syncClientAppStatus(clientId, 'plan_editing');
    return r;
  },
  // Étape 5 → 6 : la synthèse externe est prête, mais aucun plan n'existe
  // encore. L'atelier interne reste l'unique endroit qui génère le plan.
  openPlanGeneratorFromExternalSynthesis: async (clientId, externalSynthesis) => {
    const receipt = externalSynthesis && {
      synthesis_id: externalSynthesis.synthesis_id,
      synthesis_sha256: externalSynthesis.synthesis_sha256,
      profile_summary_sha256: externalSynthesis.profile_summary_sha256,
      privacy_review_confirmed: externalSynthesis.privacy_review_confirmed,
      review_status: externalSynthesis.review_status,
      reviewed_at: externalSynthesis.reviewed_at,
    };
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
      .test(receipt?.synthesis_id || '')
      || !/^[0-9a-f]{64}$/.test(receipt?.synthesis_sha256 || '')
      || !/^[0-9a-f]{64}$/.test(receipt?.profile_summary_sha256 || '')
      || receipt?.privacy_review_confirmed !== true || receipt?.review_status !== 'reviewed') {
      throw new Error('Preuve serveur de la synthèse requise.');
    }
    const r = await updateJourneyState(clientId, {
      plan_generated: false,
      external_synthesis_ready: true,
      current_step: 'plan_editing',
      plan_input_mode: 'external_anonymized_dossier_v1',
      external_dossier_synthesis: receipt,
    });
    trackStepTransition({ clientId, fromStep: 'plan_generation', toStep: 'plan_editing' });
    syncClientAppStatus(clientId, 'plan_editing');
    return r;
  },
  // Etape 6 → 7. La version cloud est la seule source de vérité : le tableau
  // React/localStorage peut être en retard sur une sauvegarde ou appartenir à
  // une ancienne consultation. La preuve reste minimale dans journey_state :
  // identifiant de version + empreinte, jamais le contenu clinique du plan.
  validatePlan: async (clientId, consultationId, expectedContentSha256, options = {}) => {
    if (!clientId) throw new Error('clientId requis');
    if (!consultationId) throw new Error('Version enregistrée requise pour valider le plan');
    if (!/^[a-f0-9]{64}$/.test(expectedContentSha256 || '')) {
      throw new Error('Empreinte de la version requise pour valider le plan');
    }
    const { data: clientRow, error: clientError } = await supabase
      .from('clients')
      .select('*')
      .eq('id', clientId)
      .maybeSingle();
    if (clientError) throw new Error(clientError.message);
    if (!clientRow) throw new Error(await expliquerLigneInvisible());

    const { data: planRow, error: planError } = await supabase
      .from('nutrition_consultations')
      .select('*')
      .eq('id', consultationId)
      .eq('client_id', clientId)
      .eq('is_deleted', false)
      .maybeSingle();
    if (planError) throw new Error(planError.message);
    if (!planRow || planRow.id !== consultationId || planRow.client_id !== clientId) {
      throw new Error('La version du plan n’est pas confirmée en base. Enregistre-la puis réessaie.');
    }

    const planText = typeof planRow.nutrition_plan === 'string' ? planRow.nutrition_plan : '';
    const supplementsText = typeof planRow.supplements === 'string' ? planRow.supplements : '';
    const contentSha256 = await sha256Hex(buildPlanValidationContent(planText, supplementsText));
    if (contentSha256 !== expectedContentSha256) {
      throw new Error('La dernière modification du plan n’est pas encore confirmée en base. Enregistre-la puis réessaie.');
    }
    const externalInput = planRow.editorial_overrides?.external_plan_input;
    const externalInputMode = clientRow.journey_state?.plan_input_mode
      === 'external_anonymized_dossier_v1';
    const standardPlan = !externalInputMode && planRow.mgd_plan_adoption_id == null;
    const externalPlan = externalInputMode && planRow.mgd_plan_adoption_id == null;
    if (options?.clinicalOverride === true && !standardPlan && !externalPlan) {
      throw new Error('Un override clinique local ne peut pas remplacer une autorité MGD.');
    }
    if (externalInputMode) {
      const currentExternalHash = await sha256Hex(planText.replace(/\r\n/g, '\n'));
      // La consultation cloud et l'autorité serveur sont les sources de vérité.
      // Les premiers dossiers externes ne copiaient pas la consultation dans
      // journey_state.external_plan_input ; exiger ce doublon rendait ces plans
      // pourtant autorisés impossibles à valider après un rechargement.
      if (!isExternalPlanInput(externalInput)) {
        throw new Error('La provenance du brouillon externe ne correspond plus au plan enregistré. Validation bloquée.');
      }
      const { data: authorityData, error: authorityError } = await supabase.rpc(
        'lire_autorite_plan_externe',
        { p_client_id: clientId, p_consultation_id: planRow.id },
      );
      if (authorityError) throw new Error(authorityError.message);
      const authority = validateExternalPlanAuthority(authorityData, {
        planHash: currentExternalHash,
        profileSummaryHash: externalInput.profile_summary_sha256,
      });
      if (!authority.authorized || !authority.persisted) {
        throw new Error('L’autorité serveur du brouillon externe est absente. Validation bloquée.');
      }
    }
    let clinicalOverridePayload = null;
    // V97.247 : le contrôle clinique s'applique aussi aux plans externes (il
    // était sauté), et la RPC scelle leur override comme pour un plan standard.
    // MGD garde son verdict serveur scellé.
    if (standardPlan || externalPlan) {
      const verdict = evaluatePlanValidationReadiness({
        planText,
        supplementsText,
        form: clientRow.form,
        journeyState: clientRow.journey_state,
      });
      // Le contrôle reste fermé par défaut. Seule la confirmation consciente
      // initiée dans le workspace Anissa peut autoriser la transition malgré
      // le verdict ; la RPC vérifie encore l'identité Anissa, les empreintes,
      // la version courante et le contrat exact avant toute mutation.
      if (!verdict.cleared && options?.clinicalOverride !== true) {
        throw new PlanValidationClinicalError(verdict);
      }
      if (!verdict.cleared) {
        clinicalOverridePayload = buildPlanClinicalOverridePayload(verdict);
      } else if (options?.clinicalOverride === true) {
        throw new Error('Aucun verdict clinique bloquant à overrider.');
      }
    }

    const serverDeliveryContract = await buildPlanDeliveryContractV1(
      { ...clientRow, id: clientId },
      planRow,
    );
    if (!serverDeliveryContract.readiness.ready) {
      throw new Error(serverDeliveryContract.readiness.blockers.join(' ')
        || 'Le contenu destiné à l’application doit être corrigé avant validation.');
    }

    const { data: persisted, error: persistError } = await supabase.rpc(
      'valider_plan_et_livrable_app_avec_decision_atomiquement',
      {
        p_client_id: clientId,
        p_consultation_id: planRow.id,
        p_expected_content_sha256: contentSha256,
        p_expected_render_inputs_sha256: serverDeliveryContract.renderInputsSha256,
        p_expected_app_artifact_sha256: serverDeliveryContract.appArtifactSha256,
        p_contract_version: PLAN_DELIVERY_CONTRACT_VERSION,
        p_clinical_override: clinicalOverridePayload,
      },
    );
    if (persistError) throw new Error(persistError.message);
    // V97.247 : une version de suivi validée reste en étape followup.
    const followupValidation = clientRow.journey_state?.current_step === 'followup';
    const expectedStep = followupValidation ? 'followup' : 'delivery';
    if (!persisted || persisted.current_step !== expectedStep
      || persisted.plan_validated !== true
      || persisted.plan_validation?.content_sha256 !== contentSha256
      || persisted.plan_validation?.consultation_id !== planRow.id
      || persisted.plan_validation?.render_inputs_sha256 !== serverDeliveryContract.renderInputsSha256
      || persisted.plan_validation?.app_artifact_sha256 !== serverDeliveryContract.appArtifactSha256
      || persisted.plan_validation?.contract_version !== PLAN_DELIVERY_CONTRACT_VERSION
      || !persisted.plan_validation?.evidence_id
      || !/^[a-f0-9]{64}$/.test(persisted.plan_validation?.form_sha256 || '')
      || persisted.plan_validation?.decision !== (clinicalOverridePayload
        ? ANISSA_CLINICAL_OVERRIDE_DECISION
        : 'cleared_no_warning')
      || typeof persisted.plan_validation?.validated_by !== 'string'
      || persisted.plan_validation.validated_by.trim() === ''
      || !Number.isFinite(Date.parse(persisted.plan_validation?.validated_at || ''))
      || !isCoherentPlanClinicalDecisionProof(persisted.plan_validation, {
        consultationId: planRow.id,
        contentSha256,
      })) {
      throw new Error('Le dossier a changé pendant la validation. Recharge la fiche et réessaie.');
    }

    validatePlanDeliveryEvidence({
      valid: true,
      evidenceId: persisted.plan_validation.evidence_id,
      contractVersion: persisted.plan_validation.contract_version,
      contentSha256: persisted.plan_validation.content_sha256,
      renderInputsSha256: persisted.plan_validation.render_inputs_sha256,
      appArtifactSha256: persisted.plan_validation.app_artifact_sha256,
      authorityKind: persisted.plan_validation.authority_kind,
    }, {
      contentSha256,
      renderInputsSha256: serverDeliveryContract.renderInputsSha256,
      appArtifactSha256: serverDeliveryContract.appArtifactSha256,
    });

    if (!followupValidation) {
      trackStepTransition({ clientId, fromStep: 'plan_editing', toStep: 'delivery' });
      syncClientAppStatus(clientId, 'delivery');
    }
    return persisted;
  },
  // Etape 7 → 8
  // AY (2026-05-11) : en plus du journey_state, on update aussi les champs
  // pack côté client (packStartedAt + packStartedAtConfirmed) pour que la
  // timeline pack du dashboard (S4 → S24) se déclenche automatiquement.
  // Avant : il fallait cliquer 'Marquer comme remis' séparément. Maintenant :
  // valider la livraison étape 7 → timeline + cockpit suivi automatiques.
  // V97.13.22 — étape 1 du workflow hybride : Anissa marque le protocole
  // comme expédié physiquement. L'app cliente bascule en "attente réception"
  // (état partiel). Le journey reste sur 'delivery' — pas encore étape 8.
  markProtocolShipped: async (clientId) => {
    const now = new Date().toISOString();
    const next = await updateJourneyState(clientId, {
      protocol_shipped: true,
      protocol_shipped_at: now,
    });
    trackStepTransition({ clientId, fromStep: 'delivery', toStep: 'delivery', direction: 'shipped' });
    syncClientAppStatus(clientId, 'protocol_shipped'); // l'app cliente passe en attente
    return next;
  },

  // V97.13.22 — étape 2 : cliente confirme dans son app qu'elle a reçu le
  // courrier (OU Anissa confirme manuellement si cliente ne clique pas).
  // Ne déclenche PAS l'activation finale — Anissa garde le contrôle.
  confirmProtocolReceived: async (clientId) => {
    const now = new Date().toISOString();
    const next = await updateJourneyState(clientId, {
      client_received_confirmed: true,
      protocol_received_at: now,
    });
    trackStepTransition({ clientId, fromStep: 'delivery', toStep: 'delivery', direction: 'received' });
    syncClientAppStatus(clientId, 'protocol_received');
    return next;
  },

  // V97.13.22 — étape 3 : Anissa active l'espace cliente (= ancien markDelivered).
  // L'app cliente débloque le protocole complet. Le journey passe à 'followup'.
  // Le pack timeline (S4 → S24) se déclenche à partir de cette date.
  markDelivered: async (clientId) => {
    if (!clientId) throw new Error('clientId requis');
    throw new Error('Choisissez explicitement Application, Document ou les deux avant la livraison');
  },

  // La confirmation de remise est elle-même une mutation autoritative. Elle
  // réserve le canal sous verrou serveur avant la livraison finale afin qu'un
  // onglet obsolète ne puisse pas écraser une publication app déjà engagée.
  confirmDocumentDelivery: async (clientId, request) => {
    if (!clientId) throw new Error('clientId requis');
    const normalized = normalizeDeliveryRequest(request);
    if (normalized.mode === DELIVERY_MODES.APP) {
      throw new Error('La confirmation document exige un mode avec document');
    }
    const { data, error } = await supabase.rpc(
      'confirmer_remise_document_plan_atomiquement',
      {
        p_client_id: clientId,
        p_delivery_mode: normalized.mode,
        p_document_channel: normalized.documentChannel,
        p_consultation_id: normalized.consultationId,
        p_content_sha256: normalized.contentSha256,
      },
    );
    if (error) throw new Error(error.message);
    if (
      data?.mode !== normalized.mode
      || data?.document_channel !== normalized.documentChannel
      || data?.consultation_id !== normalized.consultationId
      || data?.content_sha256 !== normalized.contentSha256
    ) {
      throw new Error('La confirmation de remise n a pas ete enregistree');
    }
    return data;
  },

  // Livraison autoritative par canal. Le document seul ne requiert aucune
  // publication app ; les modes contenant l'app exigent toujours la preuve
  // d'artefact approuvee par la porte de publication.
  markDeliveredByChannel: async (clientId, request) => {
    if (!clientId) throw new Error('clientId requis');
    const normalized = normalizeDeliveryRequest(request);
    const { data: persisted, error: persistError } = await supabase.rpc(
      'livrer_plan_nutritionnel_par_canal_atomiquement',
      {
        p_client_id: clientId,
        p_delivery_mode: normalized.mode,
        p_document_channel: normalized.documentChannel,
        p_consultation_id: normalized.consultationId,
        p_content_sha256: normalized.contentSha256,
      },
    );
    if (persistError) throw new Error(persistError.message);
    if (
      persisted?.journeyState?.current_step !== 'followup'
      || persisted?.journeyState?.delivered !== true
      || persisted?.packStartedAtConfirmed !== true
      || !Number.isFinite(Date.parse(persisted?.packStartedAt || ''))
      || persisted?.deliveryMode !== normalized.mode
      || (persisted?.documentChannel ?? null) !== normalized.documentChannel
    ) {
      throw new Error('La livraison et le demarrage du pack n ont pas ete enregistres');
    }
    trackStepTransition({ clientId, fromStep: 'delivery', toStep: 'followup' });
    if (normalized.mode !== DELIVERY_MODES.DOCUMENT) {
      syncClientAppStatus(clientId, 'followup');
    }
    return persisted;
  },
  // Etape 8 (terminal — pas de transition, juste flag)
  startFollowup: async (clientId) => {
    if (!clientId) throw new Error('clientId requis');
    const { data: current, error: loadError } = await supabase
      .from('clients')
      .select('journey_state, pack_type')
      .eq('id', clientId)
      .maybeSingle();
    if (loadError) throw new Error(loadError.message);
    if (!current) throw new Error(await expliquerLigneInvisible());
    const access = getPackAccess(current.pack_type);
    if (access.known !== true) {
      throw new Error('Pack absent ou inconnu : activation du suivi impossible');
    }
    if (access.continuousFollowup !== true) {
      throw new Error('Aucun suivi continu n est inclus dans ce pack ponctuel');
    }
    const r = await updateJourneyState(clientId, { followup_started: true });
    const deliveredByDocumentOnly = r?.delivery_mode === DELIVERY_MODES.DOCUMENT
      || r?.delivery_proof?.mode === DELIVERY_MODES.DOCUMENT;
    if (!deliveredByDocumentOnly) {
      syncClientAppStatus(clientId, 'followup');
    }
    return r;
  },

  // Phase K : navigation libre arriere. Permet de revenir consulter une etape
  // precedente sans perdre les booleans de validation. NE TOUCHE PAS aux
  // _validated / _skipped : juste un curseur de navigation. La cliente peut
  // reprendre la suite via les boutons de validation classiques.
  goToPreviousStep: async (clientId, currentStepKey, journeyState = null) => {
    const steps = journeyStepsForState(journeyState || {});
    const idx = steps.indexOf(currentStepKey);
    if (idx <= 0) return null;
    const previous = steps[idx - 1];
    const r = await updateJourneyState(clientId, { current_step: previous });
    trackStepTransition({ clientId, fromStep: currentStepKey, toStep: previous, direction: 'backward' });
    return r;
  },

  // Phase AF : depuis l'etape 8 Suivi (cockpit vivant), permet de re-rentrer
  // dans le cycle Editer → Livrer → Suivre sans perdre le statut "follow_up".
  // Ne reset PAS les booleans validated/delivered/etc — juste replace le
  // curseur sur plan_editing pour qu'Anissa adapte le plan depuis les
  // derniers ressentis. Apres re-publication, restartFollowup revient sur
  // l'etape 8.
  restartPlanEditing: async (clientId) => {
    const r = await updateJourneyState(clientId, { current_step: 'plan_editing' });
    trackStepTransition({ clientId, fromStep: 'followup', toStep: 'plan_editing', direction: 'backward' });
    return r;
  },
  // Apres une re-publication, retour direct a l'etape 8 cockpit.
  returnToFollowup: async (clientId) => {
    const r = await updateJourneyState(clientId, { current_step: 'followup' });
    trackStepTransition({ clientId, fromStep: 'plan_editing', toStep: 'followup' });
    return r;
  },

  // Phase AJ : enregistre une consultation (RDV cabinet/visio) effectuee.
  // L'autorite est une RPC atomique : elle verrouille le dossier et le plan
  // source, recalcule l'ordinal et le SHA, puis ajoute le compte rendu avec le
  // jalon du pack en une seule transaction.
  logConsultation: async (clientId, { notes = '', clinical = null } = {}) => {
    if (!clientId) throw new Error('clientId requis');
    if (typeof notes !== 'string' || notes.trim().length > 2400) {
      throw new Error('La note du rendez-vous doit contenir au maximum 2400 caractères.');
    }
    if (!clinical || typeof clinical !== 'object' || Array.isArray(clinical)) {
      throw new Error('Compte rendu de rendez-vous absent — prépare et valide le rendez-vous avant l’enregistrement.');
    }
    const expectedSourceConsultationId = clinical?.appointment?.source_consultation_id?.trim();
    const expectedAppointmentSha256 = clinical?.appointment?.review_content_sha256?.trim().toLowerCase();
    const expectedOrdinal = clinical?.appointment?.consultation_ordinal;
    if (!expectedSourceConsultationId || !expectedAppointmentSha256 || !Number.isInteger(expectedOrdinal)) {
      throw new Error('Compte rendu de rendez-vous absent — à valider Anissa avant l’enregistrement.');
    }

    // Validation locale ergonomique, dupliquée côté SQL sous verrou.
    const {
      normalizeMeetingPractitionerNote,
      validateReviewedClinicalRecord,
    } = await import('./consultationMeetingWorkspace');
    const validatedClinical = await validateReviewedClinicalRecord(clinical, {
      clientId,
      sourceConsultationId: expectedSourceConsultationId,
      consultationOrdinal: expectedOrdinal,
    });
    const normalizedNotes = normalizeMeetingPractitionerNote(notes);
    if (normalizedNotes !== validatedClinical.appointment.practitioner_note) {
      throw new Error('La note ne correspond plus au compte rendu confirmé — à valider Anissa.');
    }

    const { data: persisted, error: persistError } = await supabase.rpc(
      'journaliser_consultation_suivi_revue_atomiquement',
      {
        p_client_id: clientId,
        p_notes: validatedClinical.appointment.practitioner_note,
        p_clinical: validatedClinical,
        p_expected_source_consultation_id: expectedSourceConsultationId,
        p_expected_appointment_sha256: expectedAppointmentSha256,
      },
    );
    if (persistError) throw new Error(persistError.message);
    const journeyState = persisted?.journey_state;
    const savedLog = journeyState?.consultations_log;
    const saved = Array.isArray(savedLog) ? savedLog.at(-1) : null;
    const savedAppointment = saved?.clinical?.appointment;
    const expectedCycleReview = validatedClinical.appointment.cycle_review || null;
    if (!journeyState
        || savedAppointment?.client_id !== clientId
        || savedAppointment?.source_consultation_id !== expectedSourceConsultationId
        || savedAppointment?.consultation_ordinal !== expectedOrdinal
        || savedAppointment?.review_content_sha256 !== expectedAppointmentSha256
        || (expectedCycleReview && (
          savedAppointment?.occurred_confirmed !== true
          || savedAppointment?.cycle_review?.id !== expectedCycleReview.id
          || savedAppointment?.cycle_review?.step_number !== expectedCycleReview.step_number
          || savedAppointment?.cycle_review?.template_type !== expectedCycleReview.template_type
        ))) {
      throw new Error('La consultation n a pas ete enregistree ou a ete modifiee ailleurs');
    }
    return journeyState;
  },

  // Supprime la derniere entree du log (pour annuler un clic accidentel).
  removeLastConsultation: async (clientId) => {
    if (!clientId) throw new Error('clientId requis');
    const { data, error } = await supabase.rpc(
      'annuler_derniere_consultation_suivi_atomiquement',
      { p_client_id: clientId },
    );
    if (error) throw new Error(error.message);
    if (!data?.journey_state || !Array.isArray(data.journey_state.consultations_log)) {
      throw new Error('L annulation du rendez-vous n a pas ete confirmee par le serveur');
    }
    return data.journey_state;
  },
};
